import React from 'react';
import LegalLayout from '../components/LegalLayout';
import { 
  HiOutlineReceiptRefund, 
  HiOutlineCheckCircle
} from 'react-icons/hi2';

const sections = [
  { id: 'freshness-promise', title: '1. Freshness Commitment' },
  { id: 'eligibility', title: '2. Refund Eligibility' },
  { id: 'non-refundable', title: '3. Non-Refundable Cases' },
  { id: 'process-timeline', title: '4. Refund Timelines' },
  { id: 'how-to-claim', title: '5. How to Request Refund' },
];

const RefundPolicy = () => {
  return (
    <LegalLayout
      title="Refund Policy"
      subtitle="Our commitment to fresh quality produce and clear, hassle-free refund guidelines."
      lastUpdated="October 09, 2026"
      sections={sections}
    >
      <div className="space-y-8 text-gray-700">
        
        {/* Intro Note */}
        <div className="p-4 rounded-lg bg-orange-50/60 border border-orange-200/60 flex items-start gap-3">
          <HiOutlineReceiptRefund className="text-xl text-[#FF410B] shrink-0 mt-0.5" />
          <p className="text-xs sm:text-sm text-gray-700 leading-relaxed font-normal">
            At <span className="font-semibold text-gray-900">Falhari (Feastic Services Pvt Ltd)</span>, we deliver fresh, handcrafted fruit salads, cold-pressed juices, and fresh fruit platters. We take utmost care in quality, packaging, and hygiene.
          </p>
        </div>

        {/* Section 1 */}
        <section id="freshness-promise" className="scroll-mt-24 space-y-2">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">1. Our Freshness & Quality Promise</h2>
          <p className="text-sm leading-relaxed text-gray-700">
            Every Falhari meal box is prepared fresh right before dispatch using carefully washed, sanitized, and handpicked fruits. If your experience falls short of our quality standards, we offer a transparent refund process.
          </p>
        </section>

        {/* Section 2 - Refund Eligible */}
        <section id="eligibility" className="scroll-mt-24 space-y-2">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">2. Eligible Refund Scenarios</h2>
          <p className="text-sm text-gray-700 leading-relaxed mb-2">
            You are entitled to a full or partial refund in the following situations:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            
            <div className="p-3.5 rounded-lg bg-emerald-50/50 border border-emerald-200/80 space-y-1">
              <div className="flex items-center gap-1.5 text-emerald-800 font-bold text-xs">
                <HiOutlineCheckCircle className="text-base text-emerald-600 shrink-0" />
                <span>Damaged or Spoiled Produce</span>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">
                If the fruit box or bottle received is damaged, tampered with, or spoiled upon delivery.
              </p>
            </div>

            <div className="p-3.5 rounded-lg bg-emerald-50/50 border border-emerald-200/80 space-y-1">
              <div className="flex items-center gap-1.5 text-emerald-800 font-bold text-xs">
                <HiOutlineCheckCircle className="text-base text-emerald-600 shrink-0" />
                <span>Wrong Product Delivered</span>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">
                If you receive an item different from what was ordered in your confirmed invoice.
              </p>
            </div>

            <div className="p-3.5 rounded-lg bg-emerald-50/50 border border-emerald-200/80 space-y-1">
              <div className="flex items-center gap-1.5 text-emerald-800 font-bold text-xs">
                <HiOutlineCheckCircle className="text-base text-emerald-600 shrink-0" />
                <span>Unfulfilled Order</span>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">
                If an order cannot be prepared or delivered by us due to operational constraints.
              </p>
            </div>

            <div className="p-3.5 rounded-lg bg-emerald-50/50 border border-emerald-200/80 space-y-1">
              <div className="flex items-center gap-1.5 text-emerald-800 font-bold text-xs">
                <HiOutlineCheckCircle className="text-base text-emerald-600 shrink-0" />
                <span>Duplicate Charge</span>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">
                Technical glitch or double payment debited from your bank account for a single order.
              </p>
            </div>

          </div>
        </section>

        {/* Section 3 - Non-refundable */}
        <section id="non-refundable" className="scroll-mt-24 space-y-2">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">3. Non-Refundable Circumstances</h2>
          <p className="text-sm text-gray-700 leading-relaxed mb-2">Refunds will not be issued under the following conditions:</p>
          <ul className="list-disc pl-5 space-y-1.5 text-sm text-gray-700">
            <li>
              <span className="font-semibold text-gray-900">Delayed reporting:</span> Quality issues reported after <span className="font-semibold text-[#FF410B]">2 hours</span> from the time of delivery (due to the perishable nature of fresh cut fruits).
            </li>
            <li>
              <span className="font-semibold text-gray-900">Incorrect delivery info:</span> Order failed due to incorrect phone number, wrong delivery address, or recipient unreachable at destination.
            </li>
            <li>
              <span className="font-semibold text-gray-900">Change of mind:</span> Cancellation requested after order preparation or dispatch.
            </li>
            <li>
              <span className="font-semibold text-gray-900">Improper storage:</span> Quality degradation caused by keeping cut fresh fruits outside refrigeration after delivery.
            </li>
          </ul>
        </section>

        {/* Section 4 - Timelines */}
        <section id="process-timeline" className="scroll-mt-24 space-y-2">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">4. Refund Timelines & Mode of Credit</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-3.5 rounded-lg bg-slate-50 border border-slate-200 text-center">
              <span className="text-xs text-gray-500 font-semibold uppercase block mb-0.5">Claim Review</span>
              <span className="text-base font-bold text-[#FF410B]">Within 24 Hours</span>
              <span className="text-[11px] text-gray-500 block">Quality verification</span>
            </div>
            <div className="p-3.5 rounded-lg bg-slate-50 border border-slate-200 text-center">
              <span className="text-xs text-gray-500 font-semibold uppercase block mb-0.5">Bank Credit</span>
              <span className="text-base font-bold text-gray-900">5 - 7 Days</span>
              <span className="text-[11px] text-gray-500 block">Original payment source</span>
            </div>
            <div className="p-3.5 rounded-lg bg-slate-50 border border-slate-200 text-center">
              <span className="text-xs text-gray-500 font-semibold uppercase block mb-0.5">Wallet Option</span>
              <span className="text-base font-bold text-emerald-600">Instant</span>
              <span className="text-[11px] text-gray-500 block">Falhari store credit</span>
            </div>
          </div>
        </section>

        {/* Section 5 - How to Claim */}
        <section id="how-to-claim" className="scroll-mt-24 space-y-2">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">5. How to Submit a Refund Claim</h2>
          <p className="text-sm text-gray-700 leading-relaxed mb-2">To initiate a refund request, please provide:</p>
          <ol className="list-decimal pl-5 space-y-1 text-sm text-gray-700">
            <li>Your Order ID and registered mobile number.</li>
            <li>Clear photo/video proof showing the condition of the delivered box/item within 2 hours of delivery.</li>
            <li>Brief description of the issue.</li>
          </ol>
        </section>

      </div>
    </LegalLayout>
  );
};

export default RefundPolicy;
